/**
 * Annotation types based on backend/src/app/models/annotation.py
 */

import { parseAnnotationTimestamp } from '@/lib/parse-annotation-timestamp'

// Base annotation interface
interface AnnotationBase {
  id?: string
  type: 'manual' | 'pathology'
  created?: string
  created_by?: string

  // NEW: Specimen track
  specimen_track?: 'other' | 'intraoperative_rfs'

  // Shared fields (no prefix)
  tissue_topography?: string
  neoadjuvant?: 'yes' | 'no'

  // "other" track fields
  other_malignancy?: 'yes' | 'no'
  other_macro_assessment?: 'yes' | 'no'

  // "intraoperative_rfs" track - RFS Preliminary
  intraoperative_rfs_rfs_preliminary_tumor_presence?: 'yes' | 'no'
  intraoperative_rfs_rfs_preliminary_intraoperative_report?: string

  // "intraoperative_rfs" track - RFS Final
  intraoperative_rfs_rfs_final_assessment_ffpe?: 'yes' | 'no'
  intraoperative_rfs_rfs_final_malignancy?: 'yes' | 'no'
  intraoperative_rfs_rfs_final_report?: string
}

// Manual annotation type
export interface ManualAnnotation extends AnnotationBase {
  type: 'manual'
}

// Pathology annotation type with fields from pathology imports
export interface PathologyAnnotation extends AnnotationBase {
  type: 'pathology'
  case?: string
}

// Union of annotation types that carry data fields
export type DataAnnotation = ManualAnnotation | PathologyAnnotation

// Reset annotation type - invalidates all prior annotations
export interface ResetAnnotation {
  id?: string
  type: 'reset'
  created?: string
  created_by?: string
}

// Discriminated union of all annotation types
export type PathologistAnnotation = ManualAnnotation | PathologyAnnotation | ResetAnnotation

// Type guards for discriminated union
export const isManualAnnotation = (annotation: PathologistAnnotation): annotation is ManualAnnotation => {
  return annotation.type === 'manual'
}

export const isPathologyAnnotation = (annotation: PathologistAnnotation): annotation is PathologyAnnotation => {
  return annotation.type === 'pathology'
}

export const isResetAnnotation = (annotation: PathologistAnnotation): annotation is ResetAnnotation => {
  return annotation.type === 'reset'
}

/**
 * Filter annotations to only include those after the most recent ResetAnnotation.
 * If no ResetAnnotation exists, all annotations are returned.
 */
export const filterAnnotationsAfterReset = (annotations: PathologistAnnotation[]): DataAnnotation[] => {
  const isDataAnnotation = (annotation: PathologistAnnotation): annotation is DataAnnotation => !isResetAnnotation(annotation)

  // Find the most recent reset annotation by created date
  let latestResetDate = -1
  for (const annotation of annotations) {
    if (isResetAnnotation(annotation)) {
      const resetDate = parseAnnotationTimestamp(annotation.created).getTime()
      if (resetDate > latestResetDate) {
        latestResetDate = resetDate
      }
    }
  }

  if (latestResetDate === -1) return annotations.filter(isDataAnnotation)

  // Return only data annotations created after the latest reset
  return annotations.filter((annotation): annotation is DataAnnotation => {
    if (!isDataAnnotation(annotation)) return false
    const annotationDate = parseAnnotationTimestamp(annotation.created).getTime()
    return annotationDate > latestResetDate
  })
}
